import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  DragDropContext,
  Droppable,
  Draggable,
} from "react-beautiful-dnd";
import CustomDialog from "../../../../components/Dialog/CustomDialog";
import { Button } from "../../../../components/Buttons";
import IconComponent from "../../../../components/Icon/IconComponent";
import CustomDatePicker from "../../../../components/DatePicker/CustomDatePicker";
import { AutoCompleteCustom } from "../../../../components/Autocomplete/AutoCompleteCustom";
import TimeScheduler from "../../../../components/TimeScheduler/TimeScheduler";
import Checkbox from "../../../../components/Buttons/Checkbox/Checkbox";
import HtmlEditor from "../../../../components/Editor/HtmlEditor";
import { ICON_LABELS } from "../../../../utils/constants/UI";
import {
  initialTimings,
  TIMEZONE_LIST,
  EMITTER_SUCCESS,
  EMITTER_ERROR,
} from "../../../../utils/constants";
import {
  authEndpoints,
  CampaignSettingsEndpoints,
  crmEndpoints,
} from "../../../../utils/constants/httpConstants";
import { getRequest, postRequest } from "../../../../Apis/apiWrapper";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import {
  createTimingsForPayload,
  offsetForTimezoneOnDate,
} from "../../../../utils/helpers";
import classes from "./EnrollInSequenceDrawer.module.css";

// ──────────────────────────────────────────────────────────────────────────
// Static data
// ──────────────────────────────────────────────────────────────────────────

const STEP_TYPES = {
  email: {
    label: "Automated Email",
    short: "Email",
    icon: ICON_LABELS.EMAIL,
    chipClass: classes.typeChipEmail,
    iconBg: "#EFF4FF",
    iconColor: "#1E40AF",
    description: "AI-generated email sent automatically",
  },
  call: {
    label: "Call",
    short: "Call",
    icon: ICON_LABELS.CALL,
    chipClass: classes.typeChipCall,
    iconBg: "#DCFCE7",
    iconColor: "#166534",
    description: "Schedule a call with a script",
  },
  task: {
    label: "Task / To-do",
    short: "Task",
    icon: ICON_LABELS.TASK,
    chipClass: classes.typeChipTask,
    iconBg: "#FEF3C7",
    iconColor: "#92400E",
    description: "A manual task to complete",
  },
};

const STEPPER_NODES = [
  { key: 0, label: "Outreach" },
  { key: 1, label: "Structure" },
  { key: 2, label: "Context" },
  { key: 3, label: "Generate" },
  { key: 4, label: "Review" },
  { key: 5, label: "Schedule" },
];

// ──────────────────────────────────────────────────────────────────────────
// HTML sanitizer - minimal allowlist matching the AI-generated body shape
// from the backend (subject/title are plain text; body/notes contain only
// <p>, <br>, <a>, <strong>, <em>, <ul>, <ol>, <li>). This is a swappable
// façade - when DOMPurify is added to the project, replace the impl with:
//   import DOMPurify from "dompurify";
//   const sanitizeHtml = (html) => DOMPurify.sanitize(html || "");
// The allowlist below removes any <script>, <iframe>, on* handlers, and
// `javascript:` hrefs so the markup is safe to drop into innerHTML.
// ──────────────────────────────────────────────────────────────────────────
const sanitizeHtml = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  // Parse into a detached DOM tree so we can walk and prune.
  const template = document.createElement("template");
  template.innerHTML = raw;

  const ALLOWED = new Set([
    "P", "BR", "A", "STRONG", "EM", "B", "I", "U",
    "UL", "OL", "LI", "SPAN", "DIV",
  ]);
  // Per-tag attribute allowlist. Anything else is stripped.
  const ALLOWED_ATTRS = {
    A: ["href", "title", "target", "rel"],
    SPAN: ["style"],
    DIV: ["style"],
  };

  const walk = (node) => {
    if (!node) return;
    // Element nodes - prune disallowed tags, scrub attributes
    if (node.nodeType === 1) {
      if (!ALLOWED.has(node.tagName)) {
        // Replace disallowed element with its text content
        const text = document.createTextNode(node.textContent || "");
        node.replaceWith(text);
        return;
      }
      const allowedAttrs = ALLOWED_ATTRS[node.tagName] || [];
      Array.from(node.attributes).forEach((attr) => {
        if (!allowedAttrs.includes(attr.name.toLowerCase())) {
          node.removeAttribute(attr.name);
        }
      });
      // Block javascript: hrefs and force safe target rel on links
      if (node.tagName === "A") {
        const href = (node.getAttribute("href") || "").trim();
        if (/^javascript:/i.test(href)) node.removeAttribute("href");
        if (node.getAttribute("target") === "_blank") {
          node.setAttribute("rel", "noopener noreferrer");
        }
      }
    }
    // Recurse into children (snapshot first since we mutate)
    Array.from(node.childNodes).forEach(walk);
  };

  Array.from(template.content.childNodes).forEach(walk);
  return template.innerHTML;
};

// ──────────────────────────────────────────────────────────────────────────
// Light markdown → HTML converter for call/task notes.
// The AI returns call scripts / task notes as plaintext with `-` bullets
// and `\n` line breaks (not HTML). This walks the lines, groups consecutive
// bullets into <ul><li>, treats everything else as <p>, and supports a
// single level of two-space indentation as a nested <ul>.
// ──────────────────────────────────────────────────────────────────────────
const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const notesMarkdownToHtml = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  // If the text already contains HTML tags, trust it and skip the conversion.
  if (/<\/?(p|ul|ol|li|br|strong|em|b|i)\b/i.test(raw)) return raw;

  const lines = raw.replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let inList = false;
  let inSubList = false;

  const closeLists = () => {
    if (inSubList) {
      out.push("</ul>");
      inSubList = false;
    }
    if (inList) {
      out.push("</ul>");
      inList = false;
    }
  };

  lines.forEach((rawLine) => {
    const line = rawLine.replace(/\s+$/, "");
    if (!line.trim()) {
      // Blank line - close any open list and emit a small spacer.
      closeLists();
      return;
    }

    // Sub-bullet (2+ spaces of indent, then "- ")
    const subMatch = line.match(/^\s{2,}[-•]\s+(.*)$/);
    if (subMatch) {
      if (!inList) {
        out.push("<ul>");
        inList = true;
      }
      if (!inSubList) {
        out.push("<ul>");
        inSubList = true;
      }
      out.push(`<li>${escapeHtml(subMatch[1])}</li>`);
      return;
    }

    // Top-level bullet ("- " or "• " or "* ")
    const bulletMatch = line.match(/^\s*[-•*]\s+(.*)$/);
    if (bulletMatch) {
      if (inSubList) {
        out.push("</ul>");
        inSubList = false;
      }
      if (!inList) {
        out.push("<ul>");
        inList = true;
      }
      out.push(`<li>${escapeHtml(bulletMatch[1])}</li>`);
      return;
    }

    // Plain paragraph line - close lists first
    closeLists();
    out.push(`<p>${escapeHtml(line)}</p>`);
  });

  closeLists();
  return out.join("");
};

const newStepId = () =>
  `step_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// ──────────────────────────────────────────────────────────────────────────
// Horizontal Stepper
// ──────────────────────────────────────────────────────────────────────────

const HorizontalStepper = ({ activeStep }) => (
  <div className={classes.stepperBar}>
    {STEPPER_NODES.map((node, idx) => {
      const isDone = idx < activeStep;
      const isActive = idx === activeStep;
      return (
        <React.Fragment key={node.key}>
          <div className={classes.stepperNode}>
            <span
              className={`${classes.stepperDot} ${
                isDone
                  ? classes.stepperDotDone
                  : isActive
                    ? classes.stepperDotActive
                    : ""
              }`}
            >
              {isDone ? (
                <IconComponent iconLabel="check" fontSize="16px" color="#FFFFFF" />
              ) : (
                idx + 1
              )}
            </span>
            <span
              className={`${classes.stepperLabel} ${
                isActive
                  ? classes.stepperLabelActive
                  : isDone
                    ? classes.stepperLabelDone
                    : ""
              }`}
            >
              {node.label}
            </span>
          </div>
          {idx < STEPPER_NODES.length - 1 && (
            <div
              className={`${classes.stepperLine} ${
                isDone ? classes.stepperLineDone : ""
              }`}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ──────────────────────────────────────────────────────────────────────────
// Slim step row - used in Step 2 (structure-only, before content exists)
// Just shows: drag handle, STEP N pill, type chip, wait input, delete.
// No subject/body placeholders.
// ──────────────────────────────────────────────────────────────────────────

const SlimStepRow = ({
  index,
  step,
  totalSteps,
  dragHandleProps,
  onWaitChange,
  onDelete,
  readOnly = false,
}) => {
  const meta = STEP_TYPES[step.type] || STEP_TYPES.email;
  return (
    <div className={classes.slimStepRow}>
      <div className={classes.slimStepLeft}>
        {!readOnly && (
          <span
            className={classes.dragHandle}
            {...dragHandleProps}
            title="Drag to reorder"
          >
            <IconComponent
              iconLabel={ICON_LABELS.DRAG_INDICATOR}
              fontSize="18px"
            />
          </span>
        )}
        <span className={classes.stepNumberPill}>STEP {index + 1}</span>
        <span className={`${classes.typeChip} ${meta.chipClass}`}>
          <IconComponent iconLabel={meta.icon} color={meta.iconColor} fontSize="13px" />
          {meta.short}
        </span>
      </div>
      <div className={classes.slimStepRight}>
        {index > 0 && (
          <span className={classes.slimWaitInline}>
            <IconComponent iconLabel={ICON_LABELS.SCHEDULE} fontSize="13px" color="#A1A0A3" />
            Wait
            {readOnly ? (
              <span
                className={classes.slimWaitInput}
                style={{
                  background: "#F5F5F5",
                  cursor: "default",
                  borderColor: "transparent",
                }}
              >
                {step.waitDays ?? 0}
              </span>
            ) : (
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={2}
                className={classes.slimWaitInput}
                value={String(step.waitDays ?? 0)}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  // Accept digits only. Strip leading zeros so "010" becomes
                  // "10" before we parse. Empty input → 0. Clamp to 0–30.
                  const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
                  const stripped = digitsOnly.replace(/^0+(?=\d)/, "");
                  const v = Math.max(
                    0,
                    Math.min(30, stripped === "" ? 0 : Number(stripped)),
                  );
                  onWaitChange?.(v);
                }}
              />
            )}
            day{(step.waitDays ?? 0) === 1 ? "" : "s"}
          </span>
        )}
        {!readOnly && totalSteps > 1 && (
          <button
            type="button"
            className={`${classes.iconBtn} ${classes.iconBtnDanger}`}
            onClick={onDelete}
            title="Remove step"
          >
            <IconComponent iconLabel={ICON_LABELS.DELETE_OUTLINE} fontSize="16px" />
          </button>
        )}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Full step card - used in Step 4 (generated content) + Step 5 (review)
// ──────────────────────────────────────────────────────────────────────────

const StepCard = ({
  index,
  step,
  totalSteps,
  editable = false,
  editing = false,
  expanded = false,
  showWaitEditable = true,
  onWaitChange,
  onDelete,
  onEditToggle,
  onContentChange,
}) => {
  const meta = STEP_TYPES[step.type] || STEP_TYPES.email;
  const showWait = index > 0;

  // Per-step unique DOM id for HtmlEditor instances. Each step gets its own
  // contenteditable node - we read .innerHTML back via this id when the
  // user clicks Done.
  const editorId = `enroll_step_editor_${step.id}`;

  // When the user clicks the Done icon while editing an email step, snapshot
  // the HtmlEditor's innerHTML back into state. Call and task steps use
  // controlled textareas so their content is already in state.
  const handleEditToggleClick = () => {
    if (editing && step.type === "email") {
      const el = document.getElementById(editorId);
      const html = el ? el.innerHTML : null;
      if (html != null) {
        onContentChange?.({ body: html });
      }
    }
    onEditToggle?.();
  };

  // Helpers for view-mode HTML rendering - body/notes are HTML strings per
  // the backend spec, so we sanitize then innerHTML them into a styled
  // preview container that matches inbox rendering.
  const renderHtmlPreview = (html, fallbackLabel) => {
    const clean = sanitizeHtml(html || "");
    if (!clean) {
      return (
        <em style={{ color: "#A1A0A3", fontWeight: 400 }}>
          {fallbackLabel || "Not yet generated"}
        </em>
      );
    }
    return (
      <div
        className={classes.emailBodyPreview}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  };

  // Call/task notes come back as markdown-style plaintext (hyphen bullets,
  // \n line breaks). Convert to HTML first, then run through the same
  // sanitizer + preview container so spacing/bullets render correctly.
  const renderNotesPreview = (rawText, fallbackLabel) => {
    const html = notesMarkdownToHtml(rawText || "");
    return renderHtmlPreview(html, fallbackLabel);
  };

  return (
    <div className={classes.stepCard}>
      <div className={classes.stepCardHeader}>
        <div className={classes.stepCardHeaderLeft}>
          <span className={classes.stepNumberPill}>STEP {index + 1}</span>
          <span className={`${classes.typeChip} ${meta.chipClass}`}>
            <IconComponent iconLabel={meta.icon} color={meta.iconColor} fontSize="13px" />
            {meta.short}
          </span>
        </div>
        <div className={classes.stepCardHeaderRight}>
          {onEditToggle && (
            <button
              type="button"
              className={classes.iconBtn}
              onClick={handleEditToggleClick}
              title={editing ? "Done editing" : "Edit content"}
            >
              <IconComponent
                iconLabel={editing ? ICON_LABELS.SAVE : ICON_LABELS.EDIT}
                fontSize="16px"
              />
            </button>
          )}
          {editable && totalSteps > 1 && onDelete && (
            <button
              type="button"
              className={`${classes.iconBtn} ${classes.iconBtnDanger}`}
              onClick={onDelete}
              title="Remove step"
            >
              <IconComponent iconLabel={ICON_LABELS.DELETE_OUTLINE} fontSize="16px" />
            </button>
          )}
        </div>
      </div>

      <div className={classes.stepCardBody}>
        {step.type === "email" ? (
          editing ? (
            <>
              <div className={classes.field}>
                <span className={classes.fieldLabel}>Subject</span>
                <input
                  className={classes.fieldInput}
                  value={step.subject || ""}
                  onChange={(e) =>
                    onContentChange?.({ subject: e.target.value })
                  }
                  placeholder="Email subject"
                />
              </div>
              <div className={classes.field}>
                <span className={classes.fieldLabel}>Body</span>
                <div className={classes.editorHost}>
                  {/* Same two options as Compose. The unsubscribe tag is substituted when the step
                      actually sends, not when it is queued, because the token carries the
                      prospect's address. */}
                  <HtmlEditor
                    id={editorId}
                    editorHeight="220px"
                    previosData={step.body || ""}
                    isAITagNotVisible
                    isAddTagNotVisible
                    isUnsubscirbeTagVisible
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <span className={classes.stepFieldLabel}>Subject</span>
              <span className={classes.stepFieldValue}>
                {step.subject || (
                  <em style={{ color: "#A1A0A3", fontWeight: 400 }}>
                    Not yet generated
                  </em>
                )}
              </span>
              <span className={classes.stepFieldLabel} style={{ marginTop: 4 }}>
                Body
              </span>
              {renderHtmlPreview(step.body)}
            </>
          )
        ) : step.type === "call" ? (
          editing ? (
            <>
              <div className={classes.field}>
                <span className={classes.fieldLabel}>Call title</span>
                <input
                  className={classes.fieldInput}
                  value={step.subject || ""}
                  onChange={(e) =>
                    onContentChange?.({ subject: e.target.value })
                  }
                  placeholder="e.g. Discovery call with Aaron"
                />
              </div>
              <div className={classes.field}>
                <span className={classes.fieldLabel}>Call script / notes</span>
                <textarea
                  className={classes.fieldTextarea}
                  rows={10}
                  value={step.script || ""}
                  onChange={(e) =>
                    onContentChange?.({ script: e.target.value })
                  }
                  placeholder="Talking points, discovery questions, opener…"
                />
              </div>
            </>
          ) : (
            <>
              {step.subject && (
                <>
                  <span className={classes.stepFieldLabel}>Call title</span>
                  <span className={classes.stepFieldValue}>{step.subject}</span>
                </>
              )}
              <span
                className={classes.stepFieldLabel}
                style={{ marginTop: step.subject ? 4 : 0 }}
              >
                Call script
              </span>
              {renderNotesPreview(step.script)}
            </>
          )
        ) : editing ? (
          <>
            <div className={classes.field}>
              <span className={classes.fieldLabel}>Task title</span>
              <input
                className={classes.fieldInput}
                value={step.subject || ""}
                onChange={(e) =>
                  onContentChange?.({ subject: e.target.value })
                }
                placeholder="e.g. Send follow-up gift"
              />
            </div>
            <div className={classes.field}>
              <span className={classes.fieldLabel}>Task description</span>
              <textarea
                className={classes.fieldTextarea}
                rows={8}
                value={step.description || ""}
                onChange={(e) =>
                  onContentChange?.({ description: e.target.value })
                }
                placeholder="What needs to happen for this task…"
              />
            </div>
          </>
        ) : (
          <>
            {step.subject && (
              <>
                <span className={classes.stepFieldLabel}>Task title</span>
                <span className={classes.stepFieldValue}>{step.subject}</span>
              </>
            )}
            <span
              className={classes.stepFieldLabel}
              style={{ marginTop: step.subject ? 4 : 0 }}
            >
              Task
            </span>
            {renderNotesPreview(step.description)}
          </>
        )}
      </div>

      {showWait && (
        <div className={classes.waitRow}>
          <IconComponent
            iconLabel={ICON_LABELS.SCHEDULE}
            color="#A1A0A3"
            fontSize="14px"
          />
          <span className={classes.waitLabel}>Wait</span>
          {showWaitEditable ? (
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={2}
              className={classes.waitInput}
              value={String(step.waitDays ?? 0)}
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
                const stripped = digitsOnly.replace(/^0+(?=\d)/, "");
                const v = Math.max(
                  0,
                  Math.min(30, stripped === "" ? 0 : Number(stripped)),
                );
                onWaitChange?.(v);
              }}
            />
          ) : (
            <span className={classes.waitInput} style={{ background: "#F8F8F8" }}>
              {step.waitDays ?? 0}
            </span>
          )}
          <span className={classes.waitUnit}>
            day{(step.waitDays ?? 0) === 1 ? "" : "s"} after previous step
          </span>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Add-step popover (used as a standalone button + as the inline insert)
// ──────────────────────────────────────────────────────────────────────────

const AddStepPopover = ({ open, onPick, onClose, anchorClassName }) => {
  const wrapperRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      // QA reporter: clicks inside the report dialog/button must not close popovers
      if (e.target?.closest?.("[data-qa-exclude]")) return;
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className={`${classes.addStepPopover} ${anchorClassName || ""}`}
      ref={wrapperRef}
    >
      {Object.entries(STEP_TYPES).map(([typeKey, meta]) => (
        <button
          key={typeKey}
          type="button"
          className={classes.addStepOption}
          onClick={() => {
            onPick(typeKey);
            onClose?.();
          }}
        >
          <span
            className={classes.addStepOptionIcon}
            style={{ background: meta.iconBg, color: meta.iconColor }}
          >
            <IconComponent iconLabel={meta.icon} fontSize="16px" color={meta.iconColor} />
          </span>
          <span className={classes.addStepOptionLabel}>
            <span className={classes.addStepOptionTitle}>{meta.label}</span>
            <span className={classes.addStepOptionDesc}>{meta.description}</span>
          </span>
        </button>
      ))}
    </div>
  );
};

const AddStepButton = ({ onAdd, label = "Add step" }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  return (
    <div className={classes.addStepRow} ref={wrapperRef} style={{ position: "relative" }}>
      <button
        type="button"
        className={classes.addStepBtn}
        onClick={() => setOpen((v) => !v)}
      >
        <IconComponent iconLabel={ICON_LABELS.ADD} fontSize="16px" />
        {label}
      </button>
      <AddStepPopover
        open={open}
        onClose={() => setOpen(false)}
        onPick={(t) => onAdd(t)}
        anchorClassName=""
      />
      {open && <style>{`.${classes.addStepPopover}{top:calc(100% + 4px);left:50%;transform:translateX(-50%);}`}</style>}
    </div>
  );
};

// ── Insert-between affordance ─────────────────────────────────────────────
const InsertBetween = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={classes.insertBetween} style={{ position: "relative" }}>
      <button
        type="button"
        className={classes.insertBtn}
        onClick={() => setOpen((v) => !v)}
        title="Insert step here"
      >
        <IconComponent iconLabel={ICON_LABELS.ADD} fontSize="14px" color="#FF4848" />
      </button>
      <AddStepPopover
        open={open}
        onClose={() => setOpen(false)}
        onPick={(t) => onAdd(t)}
      />
      {open && <style>{`.${classes.addStepPopover}{top:calc(100% + 2px);left:50%;transform:translateX(-50%);}`}</style>}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Main Dialog
// ──────────────────────────────────────────────────────────────────────────

const EnrollInSequenceDrawer = ({ open, onClose, contactDetails, onEnrolled }) => {
  const navigate = useNavigate();
  const contactName =
    [contactDetails?.first_name, contactDetails?.last_name]
      .filter(Boolean)
      .join(" ") || "Contact";

  // The backend's get-contact-by-id endpoint returns the canonical contact
  // id under `primary_record_id`. Fall back to other shape variants in case
  // the contactDetails object was assembled elsewhere with a different key.
  const prospectId =
    contactDetails?.primary_record_id ||
    contactDetails?.contact_id ||
    contactDetails?._id ||
    contactDetails?.id ||
    null;

  // Step navigation
  const [activeStep, setActiveStep] = useState(0);

  // Ref to the scrollable canvas so we can reset its scroll position when
  // switching steps. Without this, advancing from a step that was scrolled
  // down (e.g. Step 4 / Review) lands the next step (Schedule) at the
  // previous scrollTop, hiding the "Send from" section behind the fold.
  const canvasRef = useRef(null);

  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.scrollTop = 0;
    }
  }, [activeStep]);

  // Step 1: path + selected sequence id + search query
  const [chosenPath, setChosenPath] = useState(null); // 'saved' | 'new' | null
  const [selectedSavedId, setSelectedSavedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Step 2: structure (array of {id, type, waitDays})
  const [structure, setStructure] = useState([]);

  // Step 3: contact context - single notes field, no separate LinkedIn /
  // website fields (the user pastes everything into one box).
  const [notes, setNotes] = useState("");

  // Step 4: generation state
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [contentBySlot, setContentBySlot] = useState({});
  // Top-level id returned by /generate-content. The /schedule API needs this
  // so the backend can pull email subject/body from sales_sequence_step_content_index.
  const [salesSequenceStepsId, setSalesSequenceStepsId] = useState(null);

  // Step 5: per-step editing
  const [editingId, setEditingId] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");

  // Step 6: schedule
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [sendImmediately, setSendImmediately] = useState(false);
  // Opt-out line for every email step of this enrollment. On by default; the backend stores the
  // flag per step and the send worker appends the line after the signature.
  const [includeUnsubscribeLine, setIncludeUnsubscribeLine] = useState(true);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [timezone, setTimezone] = useState([browserTz]);
  const [timezoneList, setTimezoneList] = useState([...TIMEZONE_LIST]);
  const [timings, setTimings] = useState([...initialTimings]);
  const [enrolling, setEnrolling] = useState(false);

  const [mailList, setMailList] = useState([]);
  const [emailValue, setEmailValue] = useState([]); // [email] selection
  const [emailIdState, setEmailIdState] = useState([]); // [id] selection
  const [accountIdByEmail, setAccountIdByEmail] = useState({});
  const [espByEmail, setEspByEmail] = useState({});
  const [signList, setSignList] = useState([]);
  const [signLabel, setSignLabel] = useState([]);
  const [signId, setSignId] = useState([]);

  // ── Saved sequences (real API) ────────────────────────────────────────────
  // The dialog calls `crmEndpoints.salesSequenceList` on open and stores the
  // result here. Each record has `_id`, `name`, `total_steps`, `steps[]`,
  // `created_at`, `updated_at`. Steps come back as
  // `{ step_number, step_type, wait_days }` - we map those to the internal
  // `{ id, type, waitDays }` shape when seeding the structure on Step 2.
  const [savedSequences, setSavedSequences] = useState([]);
  const [loadingSequences, setLoadingSequences] = useState(false);
  const [savingSequence, setSavingSequence] = useState(false);
  // Two-stage delete confirmation on Step 1 saved-sequence rows:
  //  - `deleteConfirmId` → which row is currently asking "Are you sure?"
  //  - `deletingId`      → which row's API call is in flight (button spinner)
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // The sequence the user picked on Step 1 (already in the savedSequences
  // array). Lets us know whether to UPDATE (has _id) or CREATE on save.
  const originSequence = useMemo(() => {
    if (!selectedSavedId) return null;
    return savedSequences.find((s) => s._id === selectedSavedId) || null;
  }, [selectedSavedId, savedSequences]);

  // Reset when dialog re-opens for a different contact / new session
  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setChosenPath(null);
    setSelectedSavedId(null);
    setSearchQuery("");
    setStructure([]);
    setNotes("");
    setGenerating(false);
    setGenerated(false);
    setContentBySlot({});
    setSalesSequenceStepsId(null);
    setEditingId(null);
    setSaveDialogOpen(false);
    setTemplateName("");
    setSendImmediately(false);
    setScheduleDate(null);
    setTimezone([browserTz]);
    setTimings([...initialTimings]);
    setMailList([]);
    setEmailValue([]);
    setEmailIdState([]);
    setAccountIdByEmail({});
    setEspByEmail({});
    setSignList([]);
    setSignLabel([]);
    setSignId([]);
    setEnrolling(false);
    setSavingSequence(false);
    setDeleteConfirmId(null);
    setDeletingId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prospectId]);

  // Fetch saved sales sequences from the API every time the dialog opens.
  // The list endpoint is tenant-scoped (server pulls tenant_id from the JWT).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingSequences(true);
        const response = await getRequest(
          navigate,
          `${crmEndpoints.salesSequenceList}?start=0&limit=50`,
        );
        if (cancelled) return;
        const records = response?.data?.data?.records || [];
        setSavedSequences(records);
      } catch (err) {
        if (!cancelled) setSavedSequences([]);
      } finally {
        if (!cancelled) setLoadingSequences(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, navigate]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getRequest(
          navigate,
          authEndpoints.getApiKeyDetails,
        );
        if (cancelled) return;
        const inboxes = response?.data?.data?.inboxes || {};
        const allInboxes = [
          ...(inboxes.gmail || []),
          ...(inboxes.outlook || []),
          ...(inboxes.zoho || []),
        ];
        const accountMap = {};
        const espMap = {};
        const inboxItems = [];
        allInboxes.forEach((m, ind) => {
          if (!m?.email || !m?._id) return;
          accountMap[m.email] = m._id;
          espMap[m.email] = m.esp;
          inboxItems.push({ id: ind + 1, label: m.email });
        });
        setAccountIdByEmail(accountMap);
        setEspByEmail(espMap);
        setMailList(inboxItems);
        if (inboxItems.length) {
          setEmailValue([inboxItems[0].label]);
          setEmailIdState([inboxItems[0].id]);
        }
      } catch {
        // Non-fatal: pickers stay empty if this fails
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, navigate]);

  // Resolve the active mailbox's account_id and fetch its signatures.
  const activeSenderEmail = emailValue?.[0] || "";
  const activeAccountId = activeSenderEmail
    ? accountIdByEmail[activeSenderEmail]
    : undefined;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const url = activeAccountId
          ? `${CampaignSettingsEndpoints.signatureList}?account_id=${activeAccountId}`
          : CampaignSettingsEndpoints.signatureList;
        const response = await getRequest(navigate, url);
        if (cancelled) return;
        const data = response?.data?.data;

        // The endpoint returns a flat array when account_id is provided,
        // and a map keyed by account_id when it isn't. Normalise both.
        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && typeof data === "object") {
          list = Object.values(data).flatMap(
            (entry) => entry?.signatures || [],
          );
        }

        const sigItems = list.map((item) => ({
          id: item?._id,
          label: item?.signature_name || item?.email || "",
          signature: item?.signature,
          is_default: !!item?.is_default,
        }));
        setSignList(sigItems);

        // Pre-select: mailbox default > first available.
        const chosen = sigItems.find((s) => s.is_default) || sigItems[0];
        if (chosen) {
          setSignId([chosen.id]);
          setSignLabel([chosen.label]);
        } else {
          setSignId([]);
          setSignLabel([]);
        }
      } catch {
        setSignList([]);
        setSignId([]);
        setSignLabel([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, navigate, activeAccountId, activeSenderEmail]);

  // ── Derived: structure merged with any generated content ─────────────────
  const stepsWithContent = useMemo(
    () =>
      structure.map((s) => ({
        ...s,
        ...(contentBySlot[s.id] || {}),
      })),
    [structure, contentBySlot],
  );

  // ── Validation per step ──────────────────────────────────────────────────
  // Context (Step 2) is informational only - Next stays enabled even if the
  // notes field is empty, since the user may not have anything to add.
  const canAdvance = useMemo(() => {
    switch (activeStep) {
      case 0:
        if (chosenPath === "saved") return !!selectedSavedId;
        if (chosenPath === "new") return true;
        return false;
      case 1:
        return structure.length > 0;
      case 2:
        return true;
      case 3:
        return generated && !generating;
      case 4:
        // Review - Next stays disabled while the generation request is
        // in flight, so the user can't advance past skeleton placeholders.
        return generated && !generating;
      case 5:
        return (
          (sendImmediately || !!scheduleDate) &&
          !!emailValue?.[0] &&
          !!signId?.[0]
        );
      default:
        return false;
    }
  }, [
    activeStep,
    chosenPath,
    selectedSavedId,
    structure,
    generated,
    generating,
    sendImmediately,
    scheduleDate,
    emailValue,
    signId,
  ]);

  // Switching path on Step 1 wipes any prior selection / seeded structure so
  // we don't end up showing "Update sequence" on a brand-new flow, or
  // dragging stale steps from the previously-picked sequence into the new
  // canvas. Also clears any in-flight save dialog state.
  const handlePickPath = (next) => {
    setChosenPath(next);
    setSelectedSavedId(null);
    setSearchQuery("");
    setStructure([]);
    setContentBySlot({});
    setSalesSequenceStepsId(null);
    setGenerated(false);
    setSaveDialogOpen(false);
    setTemplateName("");
    setDeleteConfirmId(null);
  };

  const goNext = () => {
    // Seed structure when leaving Step 0
    if (activeStep === 0) {
      if (chosenPath === "saved") {
        const seq = originSequence;
        if (seq) {
          // API shape: { step_number, step_type, wait_days } → internal
          // { id, type, waitDays }. Sort by step_number to be safe.
          const apiSteps = [...(seq.steps || [])].sort(
            (a, b) =>
              (Number(a?.step_number) || 0) - (Number(b?.step_number) || 0),
          );
          setStructure(
            apiSteps.map((s) => ({
              id: newStepId(),
              type: s.step_type,
              waitDays: Number(s.wait_days) || 0,
            })),
          );
        }
      } else if (chosenPath === "new") {
        // Truly empty canvas - user adds their first step on Step 2
        setStructure([]);
      }
    }
    setActiveStep((s) => Math.min(s + 1, STEPPER_NODES.length - 1));
  };

  const goBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  // ── Structure mutations ──────────────────────────────────────────────────
  const addStepAtEnd = (type) =>
    setStructure((prev) => [
      ...prev,
      { id: newStepId(), type, waitDays: prev.length === 0 ? 0 : 3 },
    ]);

  const insertStepAt = (atIndex, type) =>
    setStructure((prev) => {
      const next = [...prev];
      next.splice(atIndex, 0, {
        id: newStepId(),
        type,
        waitDays: atIndex === 0 ? 0 : 3,
      });
      // First step must always wait 0
      if (next[0]) next[0] = { ...next[0], waitDays: 0 };
      return next;
    });

  const removeStep = (idx) =>
    setStructure((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next[0]) next[0] = { ...next[0], waitDays: 0 };
      return next;
    });

  const setStepWait = (idx, days) =>
    setStructure((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, waitDays: days } : s)),
    );

  const handleDragEnd = (result) => {
    if (!result?.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    setStructure((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      if (next[0]) next[0] = { ...next[0], waitDays: 0 };
      return next;
    });
  };

  const updateContent = (stepId, patch) =>
    setContentBySlot((prev) => ({
      ...prev,
      [stepId]: { ...(prev[stepId] || {}), ...patch },
    }));

  // ── Generate (real endpoint) ────────────────────────────────────────────
  // Backend contract:
  //   POST crmEndpoints.salesSequenceGenerate
  //   Body: {
  //     sales_sequence_steps_index_id: <saved-sequence _id>,
  //     additional_instruction: <free-text notes>,
  //     email: <contact email>,
  //   }
  // Generation requires a persisted sequence, so if the user came via
  // "Make new" and hasn't saved yet, surface the inline save form first
  // and bail out - they need to name + save before we can call this.
  const handleGenerate = async () => {
    const savedId = originSequence?._id;
    if (!savedId) {
      EventEmitter.emit(
        EMITTER_ERROR,
        "Save the sequence first so we can generate content against it.",
      );
      // Bounce back to Structure and pop the inline save form open
      setActiveStep(1);
      setSaveDialogOpen(true);
      return;
    }
    const prospectEmail = contactDetails?.email;
    if (!prospectEmail) {
      EventEmitter.emit(EMITTER_ERROR, "Contact has no email on file");
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        sales_sequence_steps_index_id: savedId,
        additional_instruction: notes || "",
        email: prospectEmail,
      };
      // Pre-advance to Review so the user sees the skeleton state instead
      // of staring at the Generate screen while the request is in flight.
      setActiveStep(4);

      const response = await postRequest(
        navigate,
        crmEndpoints.salesSequenceGenerate,
        payload,
      );

      // Real response shape: { data: { sales_sequence_steps_id, records: [{
      // step_number, step_type, subject, body, title, notes, ... }] } }.
      // Fall back to other shapes defensively in case the contract shifts.
      const generatedRecords =
        response?.data?.data?.records ||
        response?.data?.records ||
        response?.data?.data?.steps ||
        response?.data?.steps ||
        [];
      // Capture the run id so the /schedule call can reference it.
      const runId =
        response?.data?.data?.sales_sequence_steps_id ||
        response?.data?.sales_sequence_steps_id ||
        null;
      if (runId) setSalesSequenceStepsId(runId);

      if (Array.isArray(generatedRecords) && generatedRecords.length) {
        const next = {};
        generatedRecords.forEach((g, i) => {
          // Match by step_number (1-indexed) first since it's the reliable
          // ordering signal from the backend; fall back to array position.
          const localStep =
            structure[(Number(g?.step_number) || i + 1) - 1] || structure[i];
          if (!localStep) return;

          const apiType = (g?.step_type || "").toLowerCase();
          const patch = {};
          if (apiType === "email") {
            // email: subject (plain text), body (HTML)
            patch.subject = g?.subject || "";
            patch.body = g?.body || "";
          } else if (apiType === "call") {
            // call: title (plain text) → subject slot, notes (HTML) → script
            patch.subject = g?.title || "";
            patch.script = g?.notes || "";
          } else if (apiType === "task") {
            // task: title (plain text) → subject slot, notes (HTML) → description
            patch.subject = g?.title || "";
            patch.description = g?.notes || "";
          } else {
            // Unknown type - defensively map whatever came back
            patch.subject = g?.subject || g?.title || "";
            patch.body = g?.body || "";
            patch.script = g?.script || g?.notes || "";
            patch.description = g?.description || g?.notes || "";
          }
          next[localStep.id] = patch;
        });
        setContentBySlot(next);
        setGenerated(true);
        EventEmitter.emit(
          EMITTER_SUCCESS,
          response?.data?.message || "Sequence steps generated successfully.",
        );
      } else {
        // No content came back - return to the Generate step so the user can
        // try again instead of being stranded on a blank Review screen.
        setActiveStep(3);
        EventEmitter.emit(
          EMITTER_ERROR,
          "Generation returned no content - try again.",
        );
      }
    } catch (err) {
      // On failure, bounce back to the Generate step
      setActiveStep(3);
      EventEmitter.emit(
        EMITTER_ERROR,
        err?.data?.message || "Generation failed",
      );
    } finally {
      setGenerating(false);
    }
  };

  // ── Save / update structure ─────────────────────────────────────────────
  // Builds the API payload from the current in-memory structure. The backend
  // expects:  steps: [{ step_number, step_type, wait_days }]
  const buildStepsPayload = () =>
    structure.map((s, idx) => ({
      step_number: idx + 1,
      step_type: s.type,
      wait_days: Number(s.waitDays) || 0,
    }));

  const refreshSavedSequences = async () => {
    try {
      const response = await getRequest(
        navigate,
        `${crmEndpoints.salesSequenceList}?start=0&limit=50`,
      );
      const records = response?.data?.data?.records || [];
      setSavedSequences(records);
    } catch {
      // best-effort refresh - non-fatal
    }
  };

  // ── Delete a saved sequence (whole template) ────────────────────────────
  // Two-stage: first click on trash sets `deleteConfirmId` → row shows
  // "Are you sure? [Cancel] [Delete]". Second click on Delete fires the API.
  const handleDeleteSequence = async (seqId, seqName) => {
    if (!seqId) return;
    try {
      setDeletingId(seqId);
      await postRequest(navigate, crmEndpoints.salesSequenceDelete, {
        _id: seqId,
      });
      EventEmitter.emit(
        EMITTER_SUCCESS,
        seqName ? `Deleted "${seqName}"` : "Sequence deleted",
      );
      // Optimistic local removal
      setSavedSequences((prev) => prev.filter((s) => s._id !== seqId));
      // If the deleted row was the currently-picked one, unpick it so the
      // user can't advance to Structure with a stale id.
      if (selectedSavedId === seqId) setSelectedSavedId(null);
      // Reconcile with server (in case of partial failures)
      await refreshSavedSequences();
    } catch (err) {
      EventEmitter.emit(
        EMITTER_ERROR,
        err?.data?.message || "Could not delete sequence",
      );
    } finally {
      setDeletingId(null);
      setDeleteConfirmId(null);
    }
  };

  const handleSaveStructure = () => {
    if (originSequence?._id) {
      // Update the existing structure on the server (steps overwrite - the
      // backend replaces the array entirely, per the contract).
      (async () => {
        try {
          setSavingSequence(true);
          await postRequest(navigate, crmEndpoints.salesSequenceUpdate, {
            _id: originSequence._id,
            steps: buildStepsPayload(),
          });
          EventEmitter.emit(
            EMITTER_SUCCESS,
            `Updated "${originSequence.name}"`,
          );
          await refreshSavedSequences();
        } catch (err) {
          EventEmitter.emit(
            EMITTER_ERROR,
            err?.data?.message || "Could not update sequence",
          );
        } finally {
          setSavingSequence(false);
        }
      })();
    } else {
      // No origin → ask for a name, then create
      setSaveDialogOpen(true);
    }
  };

  const handleConfirmSaveAsNew = () => {
    const trimmed = templateName.trim();
    if (!trimmed) {
      EventEmitter.emit(EMITTER_ERROR, "Give your sequence a name");
      return;
    }
    (async () => {
      try {
        setSavingSequence(true);
        const response = await postRequest(
          navigate,
          crmEndpoints.salesSequenceCreate,
          {
            name: trimmed,
            steps: buildStepsPayload(),
          },
        );
        EventEmitter.emit(EMITTER_SUCCESS, `Saved as "${trimmed}"`);
        // Pre-select the newly-created sequence so subsequent saves UPDATE it
        const newId = response?.data?.data?._id;
        if (newId) setSelectedSavedId(newId);
        setSaveDialogOpen(false);
        setTemplateName("");
        await refreshSavedSequences();
      } catch (err) {
        EventEmitter.emit(
          EMITTER_ERROR,
          err?.data?.message || "Could not save sequence",
        );
      } finally {
        setSavingSequence(false);
      }
    })();
  };

  // ── Final enroll → POST sales-sequence/schedule ─────────────────────────
  // Builds the payload to the spec the backend shared. Only email steps get
  // queued in the Lambda send queue (backend strips calls/tasks). The body
  // for each email step lives in sales_sequence_step_content_index, keyed
  // by `sales_sequence_steps_id` returned from /generate-content earlier.
  const handleEnroll = async () => {
    // ── Pre-flight validation ─────────────────────────────────────────────
    const prospectEmail = contactDetails?.email;
    if (!prospectEmail) {
      EventEmitter.emit(EMITTER_ERROR, "Contact has no email on file");
      return;
    }
    if (!salesSequenceStepsId && !originSequence?._id) {
      EventEmitter.emit(
        EMITTER_ERROR,
        "No generated content found - go back and generate first.",
      );
      return;
    }
    if (!emailValue?.[0]) {
      EventEmitter.emit(EMITTER_ERROR, "Pick a sender email");
      return;
    }
    if (!signId?.[0]) {
      EventEmitter.emit(EMITTER_ERROR, "Pick a signature");
      return;
    }
    if (!sendImmediately && !scheduleDate) {
      EventEmitter.emit(EMITTER_ERROR, "Pick a start date");
      return;
    }

    // ── Build payload ─────────────────────────────────────────────────────
    const fromEmail = emailValue[0];
    const senderAccountId = accountIdByEmail[fromEmail] || null;
    const senderEsp = espByEmail[fromEmail] || null;
    const tz = timezone?.[0] || browserTz;

    // YYYY-MM-DD for the API. scheduleDate is a JS-Date toString() - parse
    // safely so we don't pass a half-formed value.
    let scheduledDateStr = null;
    if (!sendImmediately && scheduleDate) {
      const d = new Date(scheduleDate);
      if (!Number.isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        scheduledDateStr = `${yyyy}-${mm}-${dd}`;
      }
    }

    // DST-aware offset for the selected timezone on the anchor date
    const anchorDate = scheduledDateStr ? new Date(scheduledDateStr) : new Date();
    const offset = offsetForTimezoneOnDate(tz, anchorDate);

    // Sending window in the shape the backend expects
    const timingsPayload = createTimingsForPayload(timings);

    const payload = {
      email: prospectEmail,
      sales_sequence_steps_id: salesSequenceStepsId,
      sales_sequence_steps_index_id: originSequence?._id || null,

      sender_esp: Number(senderEsp),
      from_email: fromEmail,
      sender_account_id: senderAccountId,
      signature_id: signId[0],

      schedule_now: !!sendImmediately,
      // Omit scheduled_date entirely when schedule_now is true so the
      // backend doesn't get a stale anchor
      ...(sendImmediately
        ? {}
        : { scheduled_date: scheduledDateStr }),
      timezone: tz,
      offset,
      timings: timingsPayload,
      include_unsubscribe_link: includeUnsubscribeLine,
    };

    // ── Fire ──────────────────────────────────────────────────────────────
    setEnrolling(true);
    try {
      const response = await postRequest(
        navigate,
        crmEndpoints.salesSequenceSchedule,
        payload,
      );
      // Defensive: backend may return `status: false` with a 200, treat as error.
      if (response?.data?.status === false) {
        throw {
          data: { message: response?.data?.message || "Schedule failed" },
        };
      }
      EventEmitter.emit(
        EMITTER_SUCCESS,
        response?.data?.message || "Sales Sequence scheduled",
      );
      // Let the parent refresh whatever lists it owns (e.g. the Emails
      // tab refetches get-emails so the queued sequence touches show up
      // immediately instead of after a tab switch).
      onEnrolled?.();
      onClose?.();
    } catch (err) {
      EventEmitter.emit(
        EMITTER_ERROR,
        err?.data?.message || "Could not start the sequence - try again.",
      );
    } finally {
      setEnrolling(false);
    }
  };

  // ── Timezone search ─────────────────────────────────────────────────────
  const searchTimezones = (e) => {
    const q = e?.target?.value?.toLowerCase();
    if (!q) return setTimezoneList(TIMEZONE_LIST);
    setTimezoneList(
      TIMEZONE_LIST.filter((tz) =>
        tz?.label?.toLowerCase()?.includes(q),
      ).slice(0, 100),
    );
  };

  // ── Filtered sequence list (Step 1 search) ──────────────────────────────
  // Sources from `savedSequences` (real API). API records use `_id`, `name`,
  // `total_steps`, `steps[]`. We normalize the display fields here so the
  // render code below stays simple.
  const filteredSequences = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const items = savedSequences.map((seq) => ({
      _id: seq._id,
      name: seq.name,
      description:
        seq.description ||
        `${seq.total_steps || seq.steps?.length || 0} step${
          (seq.total_steps || seq.steps?.length || 0) === 1 ? "" : "s"
        }`,
      stepCount: seq.total_steps || seq.steps?.length || 0,
      emailCount:
        (seq.steps || []).filter((s) => s?.step_type === "email").length || 0,
    }));
    if (!q) return items;
    return items.filter(
      (seq) =>
        seq.name?.toLowerCase().includes(q) ||
        seq.description?.toLowerCase().includes(q),
    );
  }, [searchQuery, savedSequences]);

  // ──────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────

  if (!open) return null;

  const headerData = {
    heading: {
      id: "enroll_outreach_title",
      label: `Enroll in Sequence - ${contactName}`,
    },
  };

  // ── Step 0: pick path ────────────────────────────────────────────────────
  const renderStep0 = () => (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <div>
          <div className={classes.sectionTitle}>Pick an outreach</div>
          <div className={classes.sectionSubtitle}>
            Start from a saved sequence or build one from scratch.
          </div>
        </div>
      </div>
      <div className={classes.sectionBody}>
        <div className={classes.pathRow}>
          <button
            type="button"
            className={`${classes.pathCard} ${
              chosenPath === "saved" ? classes.pathCardActive : ""
            }`}
            onClick={() => handlePickPath("saved")}
          >
            <span className={classes.pathCardIcon}>
              <IconComponent
                iconLabel={ICON_LABELS.LIST_ALT || "list_alt"}
                fontSize="22px"
                color="#FF4848"
              />
            </span>
            <span className={classes.pathCardTitle}>Use a saved sequence</span>
            <span className={classes.pathCardDesc}>
              Pick one of your proven outreach sequences. You can still edit
              step order and timing before enrolling.
            </span>
          </button>
          <button
            type="button"
            className={`${classes.pathCard} ${
              chosenPath === "new" ? classes.pathCardActive : ""
            }`}
            onClick={() => handlePickPath("new")}
          >
            <span className={classes.pathCardIcon}>
              <IconComponent
                iconLabel="auto_awesome"
                fontSize="22px"
                color="#FF4848"
              />
            </span>
            <span className={classes.pathCardTitle}>Make new from scratch</span>
            <span className={classes.pathCardDesc}>
              Build a custom sequence step-by-step - mix automated email,
              calls, and tasks. Save it for next time.
            </span>
          </button>
        </div>

        {chosenPath === "saved" && (
          <>
            <div className={classes.searchRow}>
              <IconComponent
                iconLabel="search"
                fontSize="18px"
                color="#A1A0A3"
              />
              <input
                type="text"
                className={classes.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sequences by name or description…"
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  className={classes.iconBtn}
                  onClick={() => setSearchQuery("")}
                  title="Clear search"
                >
                  <IconComponent iconLabel="close" fontSize="16px" />
                </button>
              )}
            </div>
            <div className={classes.savedList}>
              {loadingSequences ? (
                <div className={classes.searchEmpty}>Loading sequences…</div>
              ) : filteredSequences.length === 0 ? (
                <div className={classes.searchEmpty}>
                  {searchQuery
                    ? `No sequences match "${searchQuery}".`
                    : "No saved sequences yet - start with “Make new from scratch.”"}
                </div>
              ) : (
                filteredSequences.map((seq) => {
                  const isSelected = seq._id === selectedSavedId;
                  const isConfirming = deleteConfirmId === seq._id;
                  const isDeleting = deletingId === seq._id;
                  return (
                    <div
                      key={seq._id}
                      className={`${classes.savedItem} ${
                        isSelected ? classes.savedItemSelected : ""
                      }`}
                      onClick={() => {
                        // While confirming, the inline buttons own the row;
                        // don't let row clicks change selection.
                        if (isConfirming || isDeleting) return;
                        setSelectedSavedId(seq._id);
                      }}
                    >
                      <div className={classes.savedItemMain}>
                        <span className={classes.savedItemTitle}>
                          {seq.name}
                        </span>
                        <span className={classes.savedItemMeta}>
                          {seq.description}
                        </span>
                      </div>
                      <div className={classes.savedItemBadges}>
                        {isConfirming ? (
                          <>
                            <span
                              className={classes.savedItemMeta}
                              style={{
                                color: "#92400E",
                                fontWeight: 500,
                                marginRight: 4,
                              }}
                            >
                              Delete this sequence?
                            </span>
                            <Button
                              label="Cancel"
                              variant="ghost"
                              size="xs28"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirmId(null);
                              }}
                              disabled={isDeleting}
                            />
                            <Button
                              label="Delete"
                              variant="primary"
                              size="xs28"
                              isLoading={isDeleting}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSequence(seq._id, seq.name);
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <span className={classes.miniChip}>
                              {seq.stepCount} step
                              {seq.stepCount === 1 ? "" : "s"}
                            </span>
                            {seq.emailCount > 0 && (
                              <span
                                className={classes.miniChip}
                                style={{
                                  background: "#EFF4FF",
                                  color: "#1E40AF",
                                }}
                              >
                                {seq.emailCount} email
                                {seq.emailCount === 1 ? "" : "s"}
                              </span>
                            )}
                            <button
                              type="button"
                              className={`${classes.iconBtn} ${classes.iconBtnDanger}`}
                              title="Delete sequence"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirmId(seq._id);
                              }}
                            >
                              <IconComponent
                                iconLabel={ICON_LABELS.DELETE_OUTLINE}
                                fontSize="16px"
                              />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // ── Step 1: Structure builder (slim cards + DnD + insert-between) ───────
  const renderStep1 = () => (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <div>
          <div className={classes.sectionTitle}>Structure your sequence</div>
          <div className={classes.sectionSubtitle}>
            Add steps in the order they should fire. Drag to reorder. Hover
            between steps to insert.
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <span className={classes.miniChip}>
            {structure.length} step{structure.length === 1 ? "" : "s"}
          </span>
          {structure.length > 0 && (
            <Button
              label={originSequence?._id ? "Update sequence" : "Save sequence"}
              variant="ghost"
              size="sm36"
              LeftIcon={ICON_LABELS.SAVE}
              onClick={handleSaveStructure}
              isLoading={savingSequence && !saveDialogOpen}
            />
          )}
        </div>
      </div>

      {/* Inline save form - appears when user clicks "Save sequence" without
          an origin. Lives inside the dialog so focus is stable. */}
      {saveDialogOpen && (
        <div className={classes.inlineSaveRow}>
          <div className={classes.inlineSaveLeft}>
            <span className={classes.inlineSaveLabel}>
              Sequence name<span className={classes.fieldRequired}>*</span>
            </span>
            <input
              className={classes.fieldInput}
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g. CTO breakup cadence"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleConfirmSaveAsNew();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setSaveDialogOpen(false);
                  setTemplateName("");
                }
              }}
            />
          </div>
          <div className={classes.inlineSaveActions}>
            <Button
              label="Cancel"
              variant="ghost"
              size="sm36"
              onClick={() => {
                setSaveDialogOpen(false);
                setTemplateName("");
              }}
            />
            <Button
              label="Save"
              variant="primary"
              size="sm36"
              isLoading={savingSequence}
              onClick={handleConfirmSaveAsNew}
            />
          </div>
        </div>
      )}

      <div className={classes.sectionBody}>
        {structure.length === 0 ? (
          <>
            <div className={classes.emptyState}>
              <IconComponent
                iconLabel="auto_awesome"
                fontSize="32px"
                color="#A1A0A3"
              />
              <span className={classes.emptyStateTitle}>
                Empty canvas - pick your first step
              </span>
              <span className={classes.emptyStateDesc}>
                Start with an automated email, a call, or a task.
              </span>
            </div>
            <AddStepButton onAdd={addStepAtEnd} label="Add first step" />
          </>
        ) : (
          <>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="enroll-structure">
                {(droppableProvided) => (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                  >
                    {structure.map((step, idx) => (
                      <React.Fragment key={step.id}>
                        {idx > 0 && (
                          <InsertBetween onAdd={(t) => insertStepAt(idx, t)} />
                        )}
                        <Draggable draggableId={step.id} index={idx}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                marginBottom: 10,
                              }}
                              className={
                                snapshot.isDragging ? classes.draggingCard : ""
                              }
                            >
                              <SlimStepRow
                                index={idx}
                                step={step}
                                totalSteps={structure.length}
                                dragHandleProps={provided.dragHandleProps}
                                onWaitChange={(v) => setStepWait(idx, v)}
                                onDelete={() => removeStep(idx)}
                              />
                            </div>
                          )}
                        </Draggable>
                      </React.Fragment>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            <AddStepButton onAdd={addStepAtEnd} />
          </>
        )}
      </div>
    </div>
  );

  // ── Step 2: Context (LinkedIn Info) ─────────────────────────────────────
  const renderStep2 = () => (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <div>
          <div className={classes.sectionTitle}>
            Context for personalization
          </div>
          <div className={classes.sectionSubtitle}>
            Tell us anything that should shape the outreach to {contactName} -
            prior touchpoints, role focus, pain points, what they care about.
            The richer the context, the better the generated content.
          </div>
        </div>
      </div>
      <div className={classes.sectionBody}>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>
            Notes about this contact
          </span>
          <textarea
            className={classes.fieldTextarea}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={10}
            placeholder="Paste anything useful - LinkedIn excerpts, company positioning, prior conversations, signals that this person is in-market…"
          />
        </div>
      </div>
    </div>
  );

  // ── Step 3: Recap + Generate ────────────────────────────────────────────
  const renderStep3 = () => (
    <>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <div>
            <div className={classes.sectionTitle}>Recap before generating</div>
            <div className={classes.sectionSubtitle}>
              Quick check on what we'll use. Tweak the structure if anything
              looks off, then generate.
            </div>
          </div>
        </div>
        <div className={classes.sectionBody}>
          <div className={classes.recapList}>
            <div className={classes.recapRow}>
              <span className={classes.recapLabel}>Contact</span>
              <span className={classes.recapValue}>
                {contactName}
                {contactDetails?.email && (
                  <span className={classes.recapValueMuted}>
                    {" · "}
                    {contactDetails.email}
                  </span>
                )}
              </span>
            </div>
            <div className={classes.recapRow}>
              <span className={classes.recapLabel}>Context</span>
              <span
                className={classes.recapValue}
                style={{ whiteSpace: "pre-wrap", fontWeight: 400 }}
              >
                {notes || (
                  <span className={classes.recapValueMuted}>
                    No notes provided
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <div>
            <div className={classes.sectionTitle}>Structure</div>
            <div className={classes.sectionSubtitle}>
              Read-only snapshot of what we'll generate against. Go back to
              Structure to change order, types, or wait days.
            </div>
          </div>
          <span className={classes.miniChip}>
            {structure.length} step{structure.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className={classes.sectionBody}>
          {structure.map((step, idx) => (
            <div key={step.id} style={{ marginBottom: 10 }}>
              <SlimStepRow
                index={idx}
                step={step}
                totalSteps={structure.length}
                readOnly
              />
            </div>
          ))}

          {generating && (
            <div
              className={classes.generatingSkeleton}
              style={{ marginTop: 16 }}
            >
              {structure.map((_, i) => (
                <div key={i} className={classes.skeletonCard} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  // ── Step 4: Review & Edit ───────────────────────────────────────────────
  const renderStep4 = () => (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <div>
          <div className={classes.sectionTitle}>
            {generating ? "Generating your outreach…" : "Review & edit"}
          </div>
          <div className={classes.sectionSubtitle}>
            {generating
              ? `Writing personalized content for ${contactName}. This takes a few seconds.`
              : `This content is personalized for ${contactName}. Click the edit icon to refine any step. Wait days are editable too.`}
          </div>
        </div>
      </div>
      <div className={classes.sectionBody}>
        {generating ? (
          <div className={classes.generatingSkeleton}>
            {structure.map((_, i) => (
              <div key={i} className={classes.skeletonCard} />
            ))}
          </div>
        ) : (
          <div className={classes.timeline}>
            {stepsWithContent.map((step, idx) => (
              <div key={step.id} className={classes.timelineRow}>
                {idx > 0 && <div className={classes.timelineConnector} />}
                <StepCard
                  index={idx}
                  step={step}
                  totalSteps={structure.length}
                  expanded
                  editable
                  editing={editingId === step.id}
                  showWaitEditable
                  onEditToggle={() =>
                    setEditingId((cur) => (cur === step.id ? null : step.id))
                  }
                  onContentChange={(patch) => updateContent(step.id, patch)}
                  onWaitChange={(v) => setStepWait(idx, v)}
                  onDelete={() => removeStep(idx)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ── Step 5: Schedule ────────────────────────────────────────────────────
  const renderStep5 = () => (
    <>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <div>
            <div className={classes.sectionTitle}>Send from</div>
            <div className={classes.sectionSubtitle}>
              The mailbox and signature emails in this sequence will use.
            </div>
          </div>
        </div>
        <div className={classes.sectionBody}>
          <div className={classes.scheduleSummaryRow}>
            <div className={classes.field}>
              <span className={classes.fieldLabel}>
                Select Email<span className={classes.fieldRequired}>*</span>
              </span>
              <AutoCompleteCustom
                items={mailList}
                valueState={emailValue}
                setValueState={setEmailValue}
                idState={emailIdState}
                setIdState={setEmailIdState}
                size="sm36"
                placeholder={
                  mailList.length ? "Select an inbox" : "No inboxes connected"
                }
              />
            </div>
            <div className={classes.field}>
              <span className={classes.fieldLabel}>
                Select signature<span className={classes.fieldRequired}>*</span>
              </span>
              <AutoCompleteCustom
                items={signList}
                valueState={signLabel}
                setValueState={setSignLabel}
                idState={signId}
                setIdState={setSignId}
                size="sm36"
                placeholder={
                  signList.length
                    ? "Select a signature"
                    : "No signatures available"
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <div>
            <div className={classes.sectionTitle}>When should this start?</div>
            <div className={classes.sectionSubtitle}>
              Pick a date and a sending window. Steps fire on top of this
              schedule based on the wait days you set earlier.
            </div>
          </div>
        </div>
        <div className={classes.sectionBody}>
          <div
            className={classes.scheduleSendNow}
            onClick={() => setSendImmediately((v) => !v)}
          >
            {/* Display-only: the wrapping div owns the toggle. The inner
                native input is non-interactive so the click doesn't fire
                twice (outer onClick + label/input change). */}
            <Checkbox
              noMargin
              checked={sendImmediately}
              readOnly
              onChange={() => {}}
              style={{ pointerEvents: "none" }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#222124" }}>
                Start immediately
              </span>
              <span style={{ fontSize: 12, color: "#5F5F61" }}>
                Step 1 fires as soon as you confirm. Skip the date picker.
              </span>
            </div>
          </div>

          {/* Why this lives next to the schedule and not in the step editor: the link is minted
              per recipient at send time, so there is nothing to place in the body while editing.
              This is also the only place the sender sees it at all - the step editor only appears
              after clicking Edit on a step, which is how the option went unnoticed entirely. */}
          <div
            className={classes.scheduleSendNow}
            onClick={() => setIncludeUnsubscribeLine((v) => !v)}
          >
            <Checkbox
              noMargin
              checked={includeUnsubscribeLine}
              readOnly
              onChange={() => {}}
              style={{ pointerEvents: "none" }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#222124" }}>
                Add an unsubscribe line
              </span>
              <span style={{ fontSize: 12, color: "#5F5F61" }}>
                Appended below your signature on every email step, as a link the
                prospect can actually use.
              </span>
            </div>
          </div>

          {!sendImmediately && (
            <div className={classes.scheduleSummaryRow}>
              <div className={classes.field}>
                <span className={classes.fieldLabel}>Start date</span>
                <CustomDatePicker
                  minDate={new Date()}
                  value={scheduleDate}
                  onChange={(newValue) =>
                    setScheduleDate(newValue?.$d?.toString() || null)
                  }
                  size="sm36"
                />
              </div>
              <div className={classes.field}>
                <span className={classes.fieldLabel}>Timezone</span>
                <AutoCompleteCustom
                  items={timezoneList}
                  valueState={timezone}
                  idState={timezone}
                  setIdState={setTimezone}
                  size="sm36"
                  isSearchEnable
                  handleSearch={searchTimezones}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <div>
            <div className={classes.sectionTitle}>Sending window</div>
            <div className={classes.sectionSubtitle}>
              Which days and hours can outreach fire? We'll respect this window
              when scheduling each step.
            </div>
          </div>
        </div>
        <div className={classes.sectionBody}>
          <TimeScheduler payload={timings} getData={(e) => setTimings(e)} />
        </div>
      </div>
    </>
  );

  // ── Footer ──────────────────────────────────────────────────────────────
  const footer = (
    <div className={classes.footerBar}>
      <button
        type="button"
        className={classes.footerCancel}
        onClick={() => onClose?.()}
      >
        Cancel
      </button>
      <div className={classes.footerActions}>
        {activeStep > 0 && (
          <Button
            label="Back"
            variant="ghost"
            size="sm36"
            LeftIcon="navigate_before"
            onClick={goBack}
          />
        )}
        {activeStep === 3 ? (
          // Generate step - the primary CTA performs generation (and
          // auto-advances to Review on success). Replaces the usual Next.
          <Button
            label={generated ? "Regenerate" : "Generate batch"}
            variant="primary"
            size="sm36"
            LeftIcon="auto_awesome"
            isLoading={generating}
            disabled={generating || structure.length === 0}
            onClick={handleGenerate}
          />
        ) : activeStep < STEPPER_NODES.length - 1 ? (
          <Button
            label="Next"
            variant="primary"
            size="sm36"
            RightIcon={ICON_LABELS.NAVIGATE_NEXT}
            disabled={!canAdvance}
            onClick={goNext}
          />
        ) : (
          <Button
            label="Enroll & Start"
            variant="primary"
            size="sm36"
            RightIcon={ICON_LABELS.SEND || "send"}
            isLoading={enrolling}
            disabled={!canAdvance}
            onClick={handleEnroll}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <CustomDialog
        open={open}
        onClose={() => onClose?.()}
        width="68vw"
        remove_padding
        disableBackdropClick
        header={headerData}
        footer={footer}
      >
        <div className={classes.shell}>
          <HorizontalStepper activeStep={activeStep} />
          <div ref={canvasRef} className={classes.canvas}>
            {activeStep === 0 && renderStep0()}
            {activeStep === 1 && renderStep1()}
            {activeStep === 2 && renderStep2()}
            {activeStep === 3 && renderStep3()}
            {activeStep === 4 && renderStep4()}
            {activeStep === 5 && renderStep5()}
          </div>
        </div>
      </CustomDialog>

    </>
  );
};

export default EnrollInSequenceDrawer;
